/**
 * Why a publish or sync failed — the vocabulary the backend, the Mini App's
 * copy and the tests all share.
 *
 * Telegram's own error descriptions used to be forwarded verbatim, so a user
 * whose pack was full saw literally `Telegram: Bad Request: STICKERS_TOO_MUCH`.
 * That is an internal protocol detail: it names no action, translates into no
 * language, and reads as a crash. The structured reason travels in the error
 * envelope's `details.reason`, exactly as the media pipeline's failure reasons
 * do, and the client renders localized copy from it.
 */

export const PUBLISH_FAILURE_REASONS = [
  /** The Telegram set is at its 120-sticker ceiling. */
  'PACK_FULL',
  /** The set no longer exists on Telegram. */
  'PACK_MISSING',
  /** Telegram rejected a specific sticker's bytes or emoji. */
  'STICKER_REJECTED',
  /**
   * The local stickers could not be matched to the remote set at all, so
   * reconciliation refused to act rather than risk wiping the pack.
   */
  'MAPPING_UNRESOLVED',
  /** Telegram throttled us. Retrying shortly will work. */
  'RATE_LIMITED',
  /** Telegram was unreachable or answered with a server error. */
  'TELEGRAM_UNAVAILABLE',
  /** Another update for this same pack is already running. */
  'SYNC_IN_PROGRESS',
  /** A sticker's stored media could not be read back. */
  'MEDIA_MISSING',
  /** Anything unrecognised. */
  'UNKNOWN',
] as const;

export type PublishFailureReason = (typeof PUBLISH_FAILURE_REASONS)[number];

export function isPublishFailureReason(value: unknown): value is PublishFailureReason {
  return typeof value === 'string' && (PUBLISH_FAILURE_REASONS as readonly string[]).includes(value);
}

/**
 * Classify a Telegram Bot API error description.
 *
 * Matched on the stable uppercase error slugs Telegram emits rather than on the
 * surrounding prose, which varies. Anything unmatched is UNKNOWN and gets
 * generic copy — never the raw description.
 */
export function classifyTelegramError(input: {
  description?: string;
  errorCode?: number;
}): PublishFailureReason {
  const description = (input.description ?? '').toUpperCase();

  if (description.includes('STICKERS_TOO_MUCH')) return 'PACK_FULL';
  if (
    description.includes('STICKERSET_INVALID') ||
    description.includes('STICKERSET_NOT_FOUND') ||
    description.includes('STICKER SET NOT FOUND')
  ) {
    return 'PACK_MISSING';
  }
  if (
    description.includes('STICKER_') ||
    description.includes('EMOJI') ||
    description.includes('IMAGE_PROCESS_FAILED') ||
    description.includes('FILE_')
  ) {
    return 'STICKER_REJECTED';
  }
  if (input.errorCode === 429 || description.includes('TOO MANY REQUESTS')) return 'RATE_LIMITED';
  if (input.errorCode !== undefined && input.errorCode >= 500) return 'TELEGRAM_UNAVAILABLE';
  return 'UNKNOWN';
}

/** The i18n key describing a reason to the user. */
export function publishFailureCopyKey(reason: PublishFailureReason): string {
  switch (reason) {
    case 'PACK_FULL':
      return 'publish.error.packFull';
    case 'PACK_MISSING':
      return 'publish.error.packMissing';
    case 'STICKER_REJECTED':
      return 'publish.error.stickerRejected';
    case 'MAPPING_UNRESOLVED':
      return 'publish.error.mappingUnresolved';
    case 'RATE_LIMITED':
      return 'publish.error.rateLimited';
    case 'TELEGRAM_UNAVAILABLE':
      return 'publish.error.telegramUnavailable';
    case 'SYNC_IN_PROGRESS':
      return 'publish.error.syncInProgress';
    case 'MEDIA_MISSING':
      return 'publish.error.mediaMissing';
    default:
      return 'publish.error.unknown';
  }
}
